import React, { FunctionComponent, KeyboardEventHandler, MouseEventHandler, useMemo, useState } from "react";

import { DocumentDownloadButtonComponent, EmptyText } from "@multiweb/reactComponents";
import { bridge } from "@multiweb/reactCore/bridge";
import { downloadFile, invariant, Key } from "@multiweb/reactUtils";
import { IDataAttribute } from "@multiweb/types/react";
import { LLF_EMPTY_VALUE } from "../../constants";
import { getFilename } from "./utils";

const {
    errorHandler,
    getDocumentDownloadUrlByObjectId,
    prepareDownload,
    stopPropagation,
} = bridge;

export type DocumentDownloadComponentProps = {
    /** Název atributu, který obsahuje odkaz na dokument uložený v DMS */
    originalAttributeName: string | undefined;
    /** Hodnota atributu aplikačního objektu. */
    attributeValue: IDataAttribute["value"];
    /** Unikátní klíč záznamu. */
    recordId: string;
    /** Jméno objektu, který DataSource načítá z databáze. */
    objectName: string | undefined;
    /** Určuje, zda je komponenta vizuálně aktivní a zda s ní lze pracovat. */
    interactive?: boolean;
    /** Zobrazí načítací indikátor. */
    showLoadingIndicator: () => void;
    /** Skryje načítací indikátor. */
    hideLoadingIndicator: () => void;
};

/** Komponenta zajišťující funkcionalitu pro stažení přílohy */
export const DocumentDownloadComponent: FunctionComponent<DocumentDownloadComponentProps> = ({
    attributeValue,
    recordId,
    objectName,
    originalAttributeName,
    interactive = true,
    showLoadingIndicator,
    hideLoadingIndicator,
}) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const filename = useMemo(() => getFilename(attributeValue), [attributeValue]);

    const loadUrl = async () => {
        if (isLoading || !originalAttributeName || !objectName) return;
        setIsLoading(true);
        showLoadingIndicator();

        getDocumentDownloadUrlByObjectId(recordId, objectName, originalAttributeName)
            .then(async (value) => {
                invariant(value, "[DocumentDownloadComponent] Document is not available.");
                const canDownload = await prepareDownload(value);
                if (canDownload)
                    downloadFile(value);
            })
            .catch((error) => {
                errorHandler(error);
            })
            .finally(() => {
                setIsLoading(false);
                hideLoadingIndicator();
            });
    };

    const handleClick: MouseEventHandler<HTMLSpanElement> = (event) => {
        event.preventDefault();
        stopPropagation(event);
        loadUrl();
    };

    const handleKeyPress: KeyboardEventHandler<HTMLSpanElement> = (event) => {
        event.preventDefault();
        stopPropagation(event);
        if (event.key === Key.Enter) {
            loadUrl();
        }
    };

    return filename ? (
        <DocumentDownloadButtonComponent
            onClick={handleClick}
            onKeyPress={handleKeyPress}
            disabled={!interactive || !originalAttributeName || !objectName}
            filename={filename}
        />
    ) : (
        <EmptyText content={LLF_EMPTY_VALUE} {...{ interactive }} />
    );
};
